import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle2, ChevronDown } from 'lucide-react';
import { useWaitlist } from '../hooks/useWaitlist';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const labelClass =
  'block text-[10px] font-medium uppercase tracking-widest text-white/50 mb-1.5';
const inputClass =
  'w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-lime transition-all duration-200';

export default function WaitlistHub() {
  const { status, submit, errorMessage, successData, reset } = useWaitlist();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [os, setOs] = useState('');
  const [description, setDescription] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  const emailValid = EMAIL_REGEX.test(email);
  const isLoading = status === 'loading';
  const canSubmit = name.trim().length > 0 && emailValid && !isLoading;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmailTouched(true);
    if (!canSubmit) return;
    submit(name.trim(), email.trim(), description.trim() || undefined, os || undefined);
  };

  const emailHasError =
    emailTouched && !emailValid && email.length > 0 && status !== 'success';

  return (
    <section id="waitlist" className="relative py-32 md:py-40 bg-[#0a1628] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-6 max-w-2xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Let us guard your next{' '}
            <span className="text-emerald-400">journey</span>
          </h2>
          <p className="text-lg md:text-xl text-white/70 max-w-xl mx-auto leading-relaxed">
            Silence means safe. Let us handle the background monitoring so you can focus entirely on what matters.
          </p>
        </div>
        <AnimatePresence mode="wait">
          {status === 'success' && successData ? (
            <motion.div
              key="success"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rounded-3xl p-10 md:p-12 text-center border border-white/10 bg-white/5 backdrop-blur-2xl"
            >
              <CheckCircle2 size={56} className="text-emerald-400 mx-auto mb-6" strokeWidth={1.5} />
              <h2 className="text-3xl font-bold text-white mb-3">You're in, {successData.name}!</h2>
              <p className="text-white/50">We've sent a confirmation to <strong className="text-white/70">{successData.email}</strong>.</p>
              <button onClick={reset} className="mt-8 text-sm text-white/30 hover:text-white/60 underline underline-offset-4">
                Register another email
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              className="rounded-3xl p-8 md:p-10 border border-white/10 bg-white/5 backdrop-blur-2xl"
            >
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Secure your spot
                </h2>
                <p className="text-emerald-400/80 font-medium text-sm tracking-wide">
                  Be first when we launch
                </p>
              </div>

              {status === 'duplicate' && (
                <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-400/20">
                  <p className="text-sm font-medium text-amber-300">
                    You're already on the VIP list! We'll notify you at launch.
                  </p>
                </div>
              )}

              {status === 'server_error' && (
                <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-400/20">
                  <p className="text-sm font-medium text-red-300">
                    Something went wrong on our end. Please try again shortly.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate className="space-y-5">
                <div>
                  <label htmlFor="wl-name" className={labelClass}>
                    Full Name
                  </label>
                  <input
                    id="wl-name"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isLoading}
                    className={inputClass}
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label htmlFor="wl-email" className={labelClass}>
                    Email Address
                  </label>
                  <input
                    id="wl-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailTouched(false);
                    }}
                    onBlur={() => setEmailTouched(true)}
                    disabled={isLoading}
                    className={[
                      inputClass,
                      emailHasError || status === 'validation_error'
                        ? 'border-red-400/60 focus:ring-red-400/50'
                        : '',
                      status === 'validation_error' ? 'animate-shake' : '',
                    ].join(' ')}
                    autoComplete="email"
                  />
                  {status === 'validation_error' && errorMessage && (
                    <p className="mt-1.5 text-xs text-red-400">{errorMessage}</p>
                  )}
                  {emailHasError && status !== 'validation_error' && (
                    <p className="mt-1.5 text-xs text-red-400">
                      Please enter a valid email address.
                    </p>
                  )}
                </div>

                <AnimatePresence>
                  {name.trim().length > 0 && emailValid && (
                    <motion.div
                      initial={{ height: 0, opacity: 0, overflow: 'hidden' }}
                      animate={{ height: 'auto', opacity: 1, overflow: 'visible' }}
                      exit={{ height: 0, opacity: 0, overflow: 'hidden' }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="space-y-5"
                    >
                      <div>
                        <label className={labelClass}>Operating System (Optional)</label>
                        <div className="flex gap-3">
                          <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all duration-200 ${os === 'ios' ? 'bg-white border-brand-emerald text-brand-emerald shadow-sm' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white'}`}>
                            <input type="radio" name="os" value="ios" checked={os === 'ios'} onChange={(e) => setOs(e.target.value)} className="hidden" />
                            <span className="text-sm font-medium">iOS</span>
                          </label>
                          <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all duration-200 ${os === 'android' ? 'bg-white border-brand-emerald text-brand-emerald shadow-sm' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white'}`}>
                            <input type="radio" name="os" value="android" checked={os === 'android'} onChange={(e) => setOs(e.target.value)} className="hidden" />
                            <span className="text-sm font-medium">Android</span>
                          </label>
                        </div>
                      </div>

                      {/* Description */}
                      <div>
                        <label htmlFor="wl-description" className={labelClass}>
                          Use case (Optional)
                        </label>
                        <textarea
                          id="wl-description"
                          placeholder="Tell us how you plan to use PingAfter…"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          disabled={isLoading}
                          rows={3}
                          className={`${inputClass} resize-none`}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full py-4 rounded-xl font-semibold text-sm tracking-wide transition-all duration-300 flex items-center justify-center gap-2 bg-brand-emerald text-white hover:bg-[#157042] disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-brand-emerald/20"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Joining Waitlist...
                    </>
                  ) : (
                    'Join the Waitlist'
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-xs text-white/50">
                No spam. We'll only email you at your OS version launch.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
